/**
 * Pure validation logic for the editable numeric foreign-key fields on the
 * player-user detail page (Club Id, Linked eb_player.id).
 *
 * The entered value is a database id that must reference an existing entity.
 * We can only confirm existence server-side, so this helper maps the outcome
 * of a lookup request to a UI validation result:
 *
 *  - valid       the id exists; show the resolved name, allow submit
 *  - invalid     the id does not exist (404/400); show the error, block submit
 *  - unverified  we could not check (403/network); don't block, let the
 *                server-side validator decide on submit
 */

/**
 * Validation status shown for an editable cell. `Idle`/`Checking` are UI-only
 * lifecycle states; the rest are the outcomes of an existence lookup. These are
 * frontend concepts (derived from HTTP responses below) — there is no backend
 * enum to source them from.
 */
export enum ValidationStatus {
	Idle = "idle",
	Checking = "checking",
	Valid = "valid",
	Invalid = "invalid",
	Unverified = "unverified",
}

export type ExistenceOutcome = ValidationStatus.Valid | ValidationStatus.Invalid | ValidationStatus.Unverified;

export interface ExistenceValidationResult {
	outcome: ExistenceOutcome;
	label?: string;
	message?: string;
}

export interface ExistenceCheck<TResponse> {
	/** Performs the lookup. Resolves on success, throws on failure (carrying a numeric `status`). */
	lookup: (id: number) => Promise<TResponse>;
	/** Derives the human-readable label shown on success (e.g. club / player name). */
	getLabel: (response: TResponse) => string;
	/** Message shown when the id does not exist. */
	notFoundMessage: string;
}

const COULD_NOT_VERIFY_MESSAGE = "Could not verify — will be checked on save";

function statusOf(error: unknown): number | undefined {
	if (error && typeof error === "object" && "status" in error) {
		const status = (error as { status?: unknown }).status;
		return typeof status === "number" ? status : undefined;
	}
	return undefined;
}

export async function validateExistence<TResponse>(
	value: string,
	{ lookup, getLabel, notFoundMessage }: ExistenceCheck<TResponse>,
): Promise<ExistenceValidationResult> {
	try {
		const response = await lookup(Number(value));
		return { outcome: ValidationStatus.Valid, label: getLabel(response) };
	} catch (error) {
		const status = statusOf(error);
		if (status === 404 || status === 400) {
			return { outcome: ValidationStatus.Invalid, message: notFoundMessage };
		}
		return { outcome: ValidationStatus.Unverified, message: COULD_NOT_VERIFY_MESSAGE };
	}
}
